import { useLayoutEffect, useRef, useState, type ReactElement } from 'react';
import type { LetterPath, LetterPathPage, LetterPathStop, WeeklyLetter } from '../api';
import EmberFlame from '../../../../../shared-web/EmberFlame';
import { navigate } from '../router';
import { letterDateLine, readsLine } from '../letter-format';
import { letterAbout, talkAboutHref } from '../talk-about';
import { BottomSheet } from './BottomSheet';
import './LetterPath.css';

/**
 * The page of their paths a weekly letter opens on (§4.20, 2026-11-03 founder: A 取代周信 点击就进入了C). Each thing
 * in their life Ember has recorded is drawn as a trail of the stops where it changed, the last one where they are
 * now; the Embers written from a stop hang beside it; a stop says how many other people stood there. The letter
 * itself is one tap on (/me/letters/:id/read). Written with the letter (edge weekly-letters.js), so it is that week's.
 */
export function LetterPathView({ letter, page, path: here }: { letter: WeeklyLetter; page: LetterPathPage; path: string }): ReactElement {
  const [pathId, setPathId] = useState(page.paths[0]?.id || '');
  const [open, setOpen] = useState<{ stop: LetterPathStop; last: boolean; path: LetterPath } | null>(null);
  const shown = page.paths.find((p) => p.id === pathId) || page.paths[0];
  const reads = readsLine(letter.reads_from, letter.written_at);
  const readHref = `/me/letters/${encodeURIComponent(letter.id)}/read`;
  const opening = firstLines(letter.letter);

  return (
    <div className="letter-path" data-log-seen="me_letter_path_seen" data-log-letter-id={letter.id}>
      <header className="letter-path-hero">
        <p className="letter-path-eyebrow">Your path{reads ? ` · ${reads}` : ''}</p>
        <h1 className="letter-path-headline">{page.headline}</h1>
        <p className="letter-path-sub">The things in your life you’ve told Ember, as paths. Each stop is a moment something changed.</p>
      </header>

      {page.paths.length > 1 && (
        <nav className="letter-path-tabs" aria-label="Your paths">
          {page.paths.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`letter-path-tab${p.id === shown.id ? ' is-on' : ''}`}
              aria-pressed={p.id === shown.id}
              data-log="me_letter_path_switch"
              data-log-letter-id={letter.id}
              onClick={() => setPathId(p.id)}
            >
              {p.name}
              {p.moved && <span className="letter-path-tab-moved" aria-label="moved this week" />}
            </button>
          ))}
        </nav>
      )}

      <Trail key={shown.id} path={shown} letterId={letter.id} onOpen={(stop, last) => setOpen({ stop, last, path: shown })} />
      <p className="letter-path-hand letter-path-unwritten">The rest isn’t written yet.</p>

      <article className="letter-path-letter">
        <div className="letter-path-letter-head">
          <span className="letter-path-letter-from">
            <span className="letter-path-letter-mark" aria-hidden><EmberFlame className="letter-path-letter-flame" /></span>
            A letter from Ember
          </span>
          <span className="letter-path-letter-date">{letterDateLine(letter.written_at, 'short')}</span>
        </div>
        {opening && <p className="letter-path-letter-opening" dir="auto">{opening}</p>}
        <a
          className="letter-path-letter-read"
          href={readHref}
          data-log="me_letter_path_read"
          data-log-letter-id={letter.id}
          onClick={(e) => { e.preventDefault(); navigate(readHref); }}
        >
          Read the letter
        </a>
      </article>

      <p className="letter-path-private">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" />
        </svg>
        Only you can see your path. The numbers are just counts, never anyone’s words or names.
      </p>

      {open && <StopSheet open={open} letter={letter} back={here} onClose={() => setOpen(null)} />}
    </div>
  );
}

/** Embers pinned beside a stop on the trail, the newest; the rest are in its sheet. */
const PINNED = 2;

/** One path as a trail: stops on alternate sides, the last in the middle; the line is drawn between their dots once laid out. */
function Trail({ path, letterId, onOpen }: { path: LetterPath; letterId: string; onOpen: (stop: LetterPathStop, last: boolean) => void }): ReactElement {
  const box = useRef<HTMLOListElement>(null);
  const [line, setLine] = useState<{ all: string; recent: string; w: number; h: number } | null>(null);

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    const draw = (): void => {
      const r = el.getBoundingClientRect();
      const dots = [...el.querySelectorAll<HTMLElement>('[data-dot]')].map((n) => {
        const b = n.getBoundingClientRect();
        return { x: b.left + b.width / 2 - r.left, y: b.top + b.height / 2 - r.top, fresh: n.dataset.dot === 'new' || n.dataset.dot === 'here-new' };
      });
      let all = dots.length ? `M${dots[0].x} ${dots[0].y}` : '';
      let recent = '';
      for (let i = 1; i < dots.length; i += 1) {
        const a = dots[i - 1];
        const b = dots[i];
        const bend = Math.min(110, (b.y - a.y) * 0.6);
        const seg = Math.abs(a.x - b.x) < 2
          ? ` L${b.x} ${b.y}`
          : ` L${a.x} ${b.y - bend} C${a.x} ${b.y - bend * 0.45} ${b.x} ${b.y - bend * 0.55} ${b.x} ${b.y}`;
        all += seg;
        if (b.fresh) recent += `M${a.x} ${a.y}${seg}`;
      }
      setLine({ all, recent, w: r.width, h: r.height });
    };
    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(el);
    return () => ro.disconnect();
  }, [path.id]);

  return (
    <ol className="letter-path-trail" ref={box} aria-label={path.name}>
      {line && (
        <svg className="letter-path-line" width={line.w} height={line.h} viewBox={`0 0 ${line.w} ${line.h}`} aria-hidden>
          <path d={line.all} className="letter-path-line-wash" />
          {line.recent && <path d={line.recent} className="letter-path-line-recent" />}
          <path d={line.all} className="letter-path-line-pencil" />
        </svg>
      )}
      {path.stops.map((stop, i) => {
        const last = i === path.stops.length - 1;
        const side = last ? 'here' : i % 2 === 0 ? 'left' : 'right';
        const state = last ? 'here' : stop.new ? 'new' : 'past';
        return (
          <li key={stop.id} className={`letter-path-stop is-${side}${stop.new ? ' is-new' : ''}`}>
            <span className="letter-path-dot" data-dot={last ? (stop.new ? 'here-new' : 'here') : stop.new ? 'new' : 'past'} aria-hidden />
            <div className="letter-path-stop-body">
              {last && <p className="letter-path-hand letter-path-here">You are here</p>}
              <button
                type="button"
                className="letter-path-stop-open"
                data-log="me_letter_path_stop"
                data-log-letter-id={letterId}
                data-log-state={state}
                onClick={() => onOpen(stop, last)}
              >
                {stop.new && <span className="letter-path-new">New this week</span>}
                <span className="letter-path-stop-line" dir="auto">{stop.line}</span>
              </button>
              {!last && stop.others > 0 && <p className="letter-path-hand letter-path-others">{othersHere(stop.others)}</p>}
              {stop.embers.slice(-PINNED).map((e, k) => <Postcard key={e.id} ember={e} letterId={letterId} tilt={(i + k) % 2 === 0 ? 'l' : 'r'} big={stop.new} />)}
              {stop.embers.length > PINNED && (
                <button
                  type="button"
                  className="letter-path-hand letter-path-more"
                  data-log="me_letter_path_stop"
                  data-log-letter-id={letterId}
                  data-log-state={state}
                  onClick={() => onOpen(stop, last)}
                >
                  +{stop.embers.length - PINNED} more {stop.embers.length - PINNED === 1 ? 'Ember' : 'Embers'}
                </button>
              )}
              {last && <HereCards path={path} />}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** Where they are on the path: the place and the pull, each with the others there; neither when no one else is. */
function HereCards({ path }: { path: LetterPath }): ReactElement | null {
  const place = path.place && path.place.others > 0 ? path.place : null;
  const pull = path.pull && path.pull.others > 0 ? path.pull : null;
  if (!place && !pull) return null;
  return (
    <div className="letter-path-here-cards">
      {place && (
        <div className="letter-path-here-card">
          <PeopleGlyph />
          <div>
            <p className="letter-path-here-count">{place.others === 1 ? '1 other person has stood right here.' : `${place.others} others have stood right here.`}</p>
            <p className="letter-path-here-what" dir="auto">{place.line}</p>
          </div>
        </div>
      )}
      {pull && (
        <div className="letter-path-here-card">
          <PullGlyph />
          <div>
            <p className="letter-path-here-count">{pull.others === 1 ? '1 other person has felt this same pull,' : `${pull.others} people have felt this same pull,`}</p>
            <p className="letter-path-here-what" dir="auto">in very different lives: {pull.line}</p>
          </div>
        </div>
      )}
    </div>
  );
}

/** An Ember written from this stop, pinned beside it: its header picture, its title, its day. */
function Postcard({ ember, letterId, tilt, big }: { ember: LetterPathStop['embers'][number]; letterId: string; tilt: 'l' | 'r'; big: boolean }): ReactElement {
  const href = `/me/embers/${encodeURIComponent(ember.id)}`;
  return (
    <a
      className={`letter-path-card tilt-${tilt}${big ? ' is-big' : ''}`}
      href={href}
      data-log="me_letter_path_ember"
      data-log-letter-id={letterId}
      data-log-ember-id={ember.id}
      onClick={(e) => { e.preventDefault(); navigate(href); }}
    >
      <span className="letter-path-card-tape" aria-hidden />
      {ember.image_url ? <img className="letter-path-card-img" src={ember.image_url} alt="" loading="lazy" /> : null}
      <span className="letter-path-card-title" dir="auto">{ember.title}</span>
      <span className="letter-path-card-meta">Your Ember{ember.created_at ? ` · ${monthDay(ember.created_at)}` : ''}</span>
    </a>
  );
}

/** A stop opened: what it is, the Embers from it, how many others stood there, and a way to talk about it. */
function StopSheet({ open, letter, back, onClose }: {
  open: { stop: LetterPathStop; last: boolean; path: LetterPath };
  letter: WeeklyLetter;
  back: string;
  onClose: () => void;
}): ReactElement {
  const { stop, last, path } = open;
  const others = last && path.place ? path.place.others : stop.others;
  const talk = talkAboutHref(letterAbout(letter).about, `Ember, I was looking at my path. ${stop.line}`, back);
  return (
    <BottomSheet
      onClose={onClose}
      title={last ? 'Where you are now' : stop.new ? 'New this week' : path.name}
      description={stop.line}
      logSeen="me_letter_path_stop_seen"
      logSeenAttrs={{ 'data-log-letter-id': letter.id }}
      className="letter-path-sheet"
    >
      <div className="letter-path-sheet-body">
        {stop.embers.map((e) => (
          <a
            key={e.id}
            className="letter-path-sheet-ember"
            href={`/me/embers/${encodeURIComponent(e.id)}`}
            data-log="me_letter_path_ember"
            data-log-letter-id={letter.id}
            data-log-ember-id={e.id}
            onClick={(ev) => { ev.preventDefault(); navigate(`/me/embers/${encodeURIComponent(e.id)}`); }}
          >
            {e.image_url ? <img src={e.image_url} alt="" /> : null}
            <span className="letter-path-sheet-ember-text">
              <span className="letter-path-sheet-ember-title" dir="auto">{e.title}</span>
              <span className="letter-path-sheet-ember-meta">Your Ember{e.created_at ? ` · ${monthDay(e.created_at)}` : ''} · Read it ›</span>
            </span>
          </a>
        ))}
        {others > 0 && (
          <div className="letter-path-sheet-others">
            <p className="letter-path-sheet-label">You’re not the only one here</p>
            <p className="letter-path-sheet-count">{last ? (others === 1 ? '1 other person has stood right here.' : `${others} others have stood right here.`) : othersHere(others)}</p>
            {last && path.place?.line && <p className="letter-path-sheet-what" dir="auto">{path.place.line}</p>}
          </div>
        )}
        <a
          className="letter-path-sheet-talk"
          href={talk}
          data-log="me_letter_path_talk"
          data-log-letter-id={letter.id}
          onClick={(e) => { e.preventDefault(); navigate(talk); }}
        >
          Talk about this with Ember
        </a>
      </div>
    </BottomSheet>
  );
}

/**
 * The path in the letter's head (C): the trail of the path the page opens on, small, the newest Ember on its stop,
 * and the way back to the page.
 */
export function PathStrip({ letterId, page }: { letterId: string; page: LetterPathPage }): ReactElement | null {
  const path = page.paths[0];
  if (!path) return null;
  const stops = path.stops.slice(-7);
  const w = 300;
  const step = stops.length > 1 ? (w - 24) / (stops.length - 1) : 0;
  const pts = stops.map((s, i) => ({ x: stops.length > 1 ? 12 + i * step : w / 2, y: i % 2 === 0 ? 36 : 26, s }));
  const d = pts.map((p, i) => (i === 0 ? `M${p.x} ${p.y}` : `C${pts[i - 1].x + step / 2} ${pts[i - 1].y} ${p.x - step / 2} ${p.y} ${p.x} ${p.y}`)).join(' ');
  const newest = [...stops].reverse().find((s) => s.new && s.embers.length);
  const at = newest ? pts.find((p) => p.s.id === newest.id) : null;
  const href = `/me/letters/${encodeURIComponent(letterId)}`;
  const others = path.place?.others || 0;
  return (
    <div className="letter-path-strip">
      <p className="letter-path-strip-label">Where you are on your path</p>
      <div className="letter-path-strip-draw">
        <svg viewBox={`0 0 ${w} 56`} preserveAspectRatio="xMidYMid meet" aria-hidden>
          <path d={d} className="letter-path-line-wash" />
          <path d={d} className="letter-path-line-pencil" />
          {pts.map((p, i) => (
            <circle
              key={p.s.id}
              cx={p.x}
              cy={p.y}
              r={i === pts.length - 1 ? 6 : p.s.new ? 5 : 4}
              className={i === pts.length - 1 ? 'letter-path-strip-here' : p.s.new ? 'letter-path-strip-new' : 'letter-path-strip-past'}
            />
          ))}
        </svg>
        {at && newest?.embers[0]?.image_url && (
          <img className="letter-path-strip-thumb" src={newest.embers[0].image_url} alt="" style={{ left: `${(at.x / w) * 100}%` }} />
        )}
      </div>
      <div className="letter-path-strip-foot">
        <p>{others > 0 ? `${others} ${others === 1 ? 'other has' : 'others have'} stood where you are now.` : path.name}</p>
        <a href={href} data-log="me_letter_path_back" data-log-letter-id={letterId} onClick={(e) => { e.preventDefault(); navigate(href); }}>Your path ›</a>
      </div>
    </div>
  );
}

function othersHere(n: number): string {
  return n === 1 ? '1 other has stood here too' : `${n} others have stood here too`;
}

function monthDay(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
}

/** The letter's first lines, without its marks, for the card under the page. */
function firstLines(marked: string): string {
  const text = String(marked || '').split(/\n\s*\n/)[0]
    .replace(/\s*\[[^\]]*\]/g, '').replace(/^\?\s+/, '').replace(/^(FROM|TO)\s+/gm, '').replace(/\s+/g, ' ').trim();
  return text.length > 180 ? `${text.slice(0, 177).replace(/\s+\S*$/, '')}…` : text;
}

function PeopleGlyph(): ReactElement {
  return (
    <svg className="letter-path-glyph" viewBox="0 0 34 22" aria-hidden>
      <circle cx="8" cy="11" r="5" className="g-soft" /><circle cx="17" cy="8" r="5.5" className="g-mid" />
      <circle cx="26" cy="11" r="5" className="g-soft" /><circle cx="17" cy="17" r="3.5" className="g-you" />
    </svg>
  );
}

function PullGlyph(): ReactElement {
  return (
    <svg className="letter-path-glyph" viewBox="0 0 34 22" fill="none" aria-hidden>
      <path d="M3 15 C10 4, 24 4, 31 15" className="g-a" /><path d="M3 18 C10 9, 24 9, 31 18" className="g-b" />
      <circle cx="17" cy="7" r="3" className="g-you" />
    </svg>
  );
}
